import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, cores, statusInfo, CULTURAS_TODAS, MOTIVOS_PAUSA, MOTIVOS_ALTERACAO_PRAZO, toInputDate, fromInputDate, haptic } from "../../constants";

export default function ModalAcao({ tarefa, onFechar, onAtualizar, nomeUsuario, perfilUsuario, culturasFazenda, fazendaId }) {
  const isDono = perfilUsuario === "dono";
  const isConcluida = tarefa.status === "concluida"; // ← bloquear edição
  const [acao, setAcao] = useState("");
  const [motivoPausa, setMotivoPausa] = useState("");
  const [motivoOutro, setMotivoOutro] = useState("");
  const [observacao, setObservacao] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [abaModal, setAbaModal] = useState(isDono || isConcluida ? "historico" : "acao");
  const [editPrazoInput, setEditPrazoInput] = useState(toInputDate(tarefa.prazo));
  const [editMotivoPrazo, setEditMotivoPrazo] = useState("");
  const [editMotivoPrazoOutro, setEditMotivoPrazoOutro] = useState("");
  const [editPrioridade, setEditPrioridade] = useState(tarefa.prioridade);
  const [editTitulo, setEditTitulo] = useState(tarefa.titulo);
  const [editDescricao, setEditDescricao] = useState(tarefa.descricao);
  const [editCultura, setEditCultura] = useState(tarefa.cultura || "outro");
  const [editArea, setEditArea] = useState(tarefa.area || "");
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [operadores, setOperadores] = useState([]);
  const [operadorSelecionado, setOperadorSelecionado] = useState("");
  const [delegando, setDelegando] = useState(false);
  const [removendoDelegacao, setRemovendoDelegacao] = useState(false);

  const culturasDisponiveis = culturasFazenda && culturasFazenda.length > 0 ? culturasFazenda : CULTURAS_TODAS;
  const prazoOriginal = toInputDate(tarefa.prazo);
  const prazoAlterado = editPrazoInput && editPrazoInput !== prazoOriginal;
  const prazoExistia = tarefa.prazo && tarefa.prazo !== "A definir";
  const podeDelegar = ["adm", "gestor"].includes(perfilUsuario);
  const podeEditar = ["adm", "gestor"].includes(perfilUsuario) && !isConcluida;

  useEffect(() => {
    axios.get(`${API}/tarefas/${tarefa.id}/historico`).then(res => setHistorico(res.data));
    if (podeDelegar) axios.get(`${API}/usuarios/${fazendaId}/operadores`).then(res => setOperadores(res.data)).catch(() => setOperadores([]));
  }, [tarefa.id, fazendaId, podeDelegar]);

  const executar = async () => {
    if (acao === "pausar" && !motivoPausa) return alert("Selecione o motivo da pausa!");
    if (acao === "pausar" && motivoPausa === "Outro" && !motivoOutro) return alert("Descreva o motivo!");
    setCarregando(true);
    try {
      await axios.post(`${API}/tarefas/${tarefa.id}/acao`, { acao, motivo_pausa: motivoPausa, motivo_pausa_outro: motivoOutro, observacao, usuario: nomeUsuario });
      if (acao === "concluir") haptic([10, 50, 100, 50, 200]);
      onFechar();
    } catch { alert("Erro ao executar ação."); }
    setCarregando(false);
  };

  const delegar = async () => {
    if (!operadorSelecionado) return alert("Selecione um operador!");
    setDelegando(true);
    try {
      await axios.post(`${API}/tarefas/${tarefa.id}/delegar`, { operador_id: parseInt(operadorSelecionado), usuario: nomeUsuario });
      haptic([10, 30, 10]); onFechar();
    } catch { onFechar(); }
    setDelegando(false);
  };

  const removerDelegacao = async () => {
    if (!window.confirm("Remover delegação?")) return;
    setRemovendoDelegacao(true);
    try { await axios.patch(`${API}/tarefas/${tarefa.id}/remover-delegacao`, { usuario: nomeUsuario }); onFechar(); }
    catch { alert("Erro ao remover delegação."); }
    setRemovendoDelegacao(false);
  };

  const salvarEdicao = async () => {
    if (prazoAlterado && prazoExistia && !editMotivoPrazo) return alert("Informe o motivo da alteração do prazo!");
    if (prazoAlterado && prazoExistia && editMotivoPrazo === "Outro" && !editMotivoPrazoOutro) return alert("Descreva o motivo.");
    setSalvandoEdicao(true);
    try {
      const motivo = editMotivoPrazo === "Outro" ? editMotivoPrazoOutro : editMotivoPrazo;
      await axios.patch(`${API}/tarefas/${tarefa.id}/editar`, {
        prazo: editPrazoInput ? fromInputDate(editPrazoInput) : null,
        motivo_alteracao_prazo: (prazoAlterado && prazoExistia) ? motivo : null,
        prioridade: editPrioridade, titulo: editTitulo, descricao: editDescricao, cultura: editCultura, area: editArea, usuario: nomeUsuario
      });
      onFechar();
    } catch (e) { alert(e.response?.data?.detail || "Erro ao salvar."); }
    setSalvandoEdicao(false);
  };

  const acoesDisponiveis = () => {
    if (["nao_planejada", "planejada", "atrasada"].includes(tarefa.status)) return [["iniciar", "▶️ Iniciar execução"]];
    if (tarefa.status === "em_execucao") return [["pausar", "⏸️ Pausar"], ["concluir", "✅ Concluir"]];
    if (tarefa.status === "pausada") return [["retomar", "▶️ Retomar"], ["concluir", "✅ Concluir"]];
    return [];
  };

  const tipoIcone = { criada: "🤖", iniciada: "▶️", pausada: "⏸️", retomada: "▶️", concluida: "✅", editada: "✏️", planejada: "📋", pendente: "⏳", atrasada: "🔴", delegada: "👷" };
  // Aba editar oculta se concluída
  const abas = isDono ? [["historico", "📋 Histórico"]] : [
    ["acao", "⚡ Ação"],
    ...(!isConcluida && podeEditar ? [["editar", "✏️ Editar"]] : []),
    ...(podeDelegar && !isConcluida ? [["delegar", "👷 Delegar"]] : []),
    ["historico", "📋 Histórico"]
  ];
  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition";
  const s = statusInfo[tarefa.status];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl p-5 w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 mr-3">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-snug">{tarefa.titulo}</h2>
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-1 font-medium ${s?.bg} ${s?.text}`}>{s?.emoji} {s?.label}</span>
            {isConcluida && <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">🔒 Somente leitura</span>}
          </div>
          <button onClick={onFechar} className="text-gray-400 text-xl min-h-11 min-w-11 flex items-center justify-center rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800">×</button>
        </div>
        {isDono && <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl mb-3 text-xs text-amber-700 dark:text-amber-300 font-medium">👑 Visualização somente leitura</div>}
        {tarefa.delegado_para_nome && <div className="p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-xl mb-3 text-xs text-green-700 dark:text-green-400 font-medium">👷 Delegada para: {tarefa.delegado_para_nome}</div>}

        <div className="flex gap-1 mb-5 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl overflow-x-auto">
          {abas.map(([id, label]) => (
            <button key={id} onClick={() => setAbaModal(id)} className={`flex-shrink-0 flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition min-h-11 ${abaModal === id ? "bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}>{label}</button>
          ))}
        </div>

        {abaModal === "acao" && !isDono && (
          <div>
            {isConcluida && <p className="text-sm text-gray-500 text-center py-4">Esta tarefa já foi concluída. ✅</p>}
            {!isConcluida && acoesDisponiveis().length === 0 && <p className="text-sm text-gray-500 text-center py-4">Nenhuma ação disponível.</p>}
            {!isConcluida && acoesDisponiveis().length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selecione a ação:</p>
                <div className="flex gap-2 flex-wrap">
                  {acoesDisponiveis().map(([id, label]) => (
                    <button key={id} onClick={() => setAcao(id)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm font-semibold transition min-h-11 ${acao === id ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400" : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            {acao === "pausar" && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Motivo da pausa:</p>
                <div className="space-y-2">
                  {MOTIVOS_PAUSA.map(m => (
                    <label key={m} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer min-h-11">
                      <input type="radio" name="motivo" value={m} checked={motivoPausa === m} onChange={() => setMotivoPausa(m)} className="w-4 h-4 accent-green-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{m}</span>
                    </label>
                  ))}
                </div>
                {motivoPausa === "Outro" && <input value={motivoOutro} onChange={e => setMotivoOutro(e.target.value)} placeholder="Descreva..." className={inputClass + " mt-2"} />}
              </div>
            )}
            {acao === "concluir" && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Observação (opcional):</p>
                <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} className={inputClass + " resize-none"} placeholder="Descreva como foi..." />
              </div>
            )}
            {acao && !isConcluida && <button onClick={executar} disabled={carregando} className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 rounded-xl text-sm transition min-h-11">{carregando ? "Salvando..." : "Confirmar ação"}</button>}
          </div>
        )}

        {abaModal === "delegar" && podeDelegar && !isDono && !isConcluida && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Selecione o operador responsável:</p>
            {operadores.length === 0 && <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-xs text-amber-700 dark:text-amber-300 text-center">⚠️ Nenhum operador cadastrado nesta fazenda.</div>}
            {operadores.length > 0 && (
              <div className="space-y-2">
                {operadores.map(op => (
                  <label key={op.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition min-h-11 ${operadorSelecionado === String(op.id) ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-100 dark:border-gray-700 hover:border-gray-200"}`}>
                    <input type="radio" name="operador" value={String(op.id)} checked={operadorSelecionado === String(op.id)} onChange={() => setOperadorSelecionado(String(op.id))} className="w-4 h-4 accent-green-600" />
                    <div><p className="text-sm font-semibold text-gray-900 dark:text-white">👷 {op.nome}</p>{op.cargo && <p className="text-xs text-gray-500 dark:text-gray-400">{op.cargo}</p>}</div>
                  </label>
                ))}
                <button onClick={delegar} disabled={delegando || !operadorSelecionado} className="w-full bg-green-600 disabled:bg-green-400 text-white font-semibold py-3 rounded-xl text-sm transition mt-2 min-h-11">{delegando ? "Delegando..." : "👷 Confirmar delegação"}</button>
                {tarefa.delegado_para_nome && <button onClick={removerDelegacao} disabled={removendoDelegacao} className="w-full bg-red-50 dark:bg-red-900/20 text-red-600 border border-red-100 dark:border-red-800 font-semibold py-3 rounded-xl text-sm transition min-h-11">{removendoDelegacao ? "Removendo..." : "❌ Remover delegação"}</button>}
              </div>
            )}
          </div>
        )}

        {abaModal === "editar" && podeEditar && !isDono && !isConcluida && (
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Título</label><input value={editTitulo} onChange={e => setEditTitulo(e.target.value)} className={inputClass + " mt-1.5"} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descrição</label><textarea value={editDescricao} onChange={e => setEditDescricao(e.target.value)} rows={3} className={inputClass + " mt-1.5 resize-none"} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cultura</label><select value={editCultura} onChange={e => setEditCultura(e.target.value)} className={inputClass + " mt-1.5"}>{culturasDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Área / Local</label><input value={editArea} onChange={e => setEditArea(e.target.value)} className={inputClass + " mt-1.5"} /></div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{prazoExistia ? "Prazo" : "📅 Definir prazo"}</label>
              <input type="date" value={editPrazoInput} onChange={e => setEditPrazoInput(e.target.value)}
                className={`${inputClass} mt-1.5 ${prazoAlterado && prazoExistia ? "border-amber-400" : ""}`} />
              {prazoExistia && <p className="text-xs text-gray-400 mt-1">Atual: {tarefa.prazo}</p>}
            </div>
            {prazoAlterado && prazoExistia && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">⚠️ Motivo da alteração:</p>
                <div className="space-y-1.5">
                  {MOTIVOS_ALTERACAO_PRAZO.map(m => (
                    <label key={m} className="flex items-center gap-2 cursor-pointer min-h-11">
                      <input type="radio" name="motivo_prazo" value={m} checked={editMotivoPrazo === m} onChange={() => setEditMotivoPrazo(m)} className="w-4 h-4 accent-amber-600" />
                      <span className="text-xs text-gray-700 dark:text-gray-300">{m}</span>
                    </label>
                  ))}
                </div>
                {editMotivoPrazo === "Outro" && <input value={editMotivoPrazoOutro} onChange={e => setEditMotivoPrazoOutro(e.target.value)} className={inputClass + " mt-2"} />}
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Prioridade</label>
              <div className="flex gap-2 mt-1.5">
                {["baixa", "media", "alta"].map(p => (
                  <button key={p} onClick={() => setEditPrioridade(p)} className="flex-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition min-h-11"
                    style={editPrioridade === p ? { borderColor: cores[p], color: cores[p], backgroundColor: `${cores[p]}15` } : { borderColor: "#e5e7eb", color: "#6b7280" }}>● {p}</button>
                ))}
              </div>
            </div>
            <button onClick={salvarEdicao} disabled={salvandoEdicao} className="w-full bg-green-600 text-white font-semibold py-3 rounded-xl text-sm transition min-h-11">{salvandoEdicao ? "Salvando..." : "💾 Salvar alterações"}</button>
          </div>
        )}

        {abaModal === "historico" && (
          <div>
            {historico.length === 0 && <div className="text-center py-8"><p className="text-3xl mb-2">📋</p><p className="text-sm font-semibold text-gray-600 dark:text-gray-300">Nenhum registro ainda</p></div>}
            <div className="space-y-3">
              {historico.map((h, i) => (
                <div key={i} className="flex gap-3 pb-3 border-b border-gray-50 dark:border-gray-800 last:border-0">
                  <div className="text-base flex-shrink-0">{tipoIcone[h.tipo] || "📝"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{h.descricao}</p>
                    {h.motivo_pausa && <p className="text-xs text-red-600 mt-0.5">Motivo: {h.motivo_pausa}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">{h.usuario} · {new Date(h.criado_em).toLocaleString("pt-BR")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
